import { formatCurrency } from '@asn/formatters';

import { ChangeDetectionStrategy, Component, ViewEncapsulation, input, computed, Signal } from '@angular/core';
import { NgClass } from '@angular/common';

import { DisplaySize, Variant } from './display.model';

@Component({
    selector: 'ap-amount-display',
    templateUrl: './display.component.html',
    styleUrls: ['./display.component.scss'],
    preserveWhitespaces: false,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgClass]
})
export class ApAmountDisplayComponent {
    /**
     * Required - Value to display
     */
    public readonly value = input.required<number>();

    /**
     * Is the amount cancelled?
     */
    public readonly cancelled = input(false);

    /**
     * Should it contain contrast?
     * Defaults to `false`.
     */
    public readonly contrast = input(false);

    /**
     * ISO 4217 currency code
     * Defaults to `EUR`.
     */
    public readonly currencyCode = input('EUR');

    /**
     * Different display options. Possible values: `code` | `symbol` | `symbol-narrow`.
     * Defaults to `Symbol`.
     */
    public readonly display = input<'code' | 'symbol' | 'symbol-narrow'>('symbol');

    /**
     * Should the symbol be shown or not?
     */
    public readonly displaySymbol = input(true);

    /**
     * Should text be emphasized?
     */
    public readonly emphasize = input(false);

    /**
     * Is the amount an estimate?
     */
    public readonly estimate = input(false);

    /**
     * Variant of amount-display. Accepts `Variant` enum values.
     * Defaults to `Variant.DEFAULT`
     */
    public readonly variant = input<Variant>(Variant.DEFAULT);

    /**
     * Size of amounts. Possible values come from `DisplaySize` enum
     */
    public readonly size = input<DisplaySize>('default');

    /**
     * Minimum amount of integer digits.
     * Defaults to `1`.
     */
    public readonly minIntegerDigits = input(1);

    /**
     * Minimum amount of fraction digits.
     * Defaults to `2`.
     */
    public readonly minFractionDigits = input(2);

    /**
     * Minimum amount of integer digits.
     * Defaults to `2`.
     */
    public readonly maxFractionDigits = input(2);

    /**
     * Formatted value to display
     * @internal
     */
    public readonly displayValue = computed(() => this.formatValue());

    /**
     * @internal
     */
    public readonly hasNegativeAccent: Signal<boolean> = computed(() => this.value() < 0 && this.variant() === Variant.NEGATIVE_ACCENT);

    /**
     * @internal
     */
    public readonly hasPositiveAccent: Signal<boolean> = computed(() => this.value() > 0 && this.variant() === Variant.POSITIVE_ACCENT);

    /**
     * @internal
     */
    public readonly hasNegativeContrast: Signal<boolean> = computed(() => this.value() < 0 && this.contrast());

    /**
     * @internal
     */
    public readonly hasPositiveContrast: Signal<boolean> = computed(() => this.value() >= 0 && this.contrast());

    /**
     * @internal
     */
    public readonly hasExtraPadding: Signal<boolean> = computed(() => this.variant() === Variant.POSITIVE_ACCENT);

    private readonly isPositiveDisplay: Signal<boolean> = computed(
        () => this.value() > 0 && (this.variant() === Variant.POSITIVE_ACCENT || this.variant() === Variant.EXPLICIT)
    );

    private formatValue(): string {
        let formattedValue: string;
        const digitInfo = `${this.minIntegerDigits()}.${this.minFractionDigits()}-${this.maxFractionDigits()}`;
        formattedValue = formatCurrency(this.value(), this.currencyCode(), this.display(), digitInfo, 'nl');
        if (this.shouldRemoveSymbol()) {
            formattedValue = formattedValue.slice(2);
        }

        if (this.isPositiveDisplay()) {
            formattedValue = formattedValue.replace(/(\d{1}.+)/, '+$1');
        }

        return formattedValue;
    }

    private readonly shouldRemoveSymbol: Signal<boolean> = computed(() => !this.displaySymbol() && this.display() === 'symbol');
}
